/**
 * EN / ES translations for the /workflow-automation service page.
 * Imported and merged into the main translations object.
 */
export const workflowAutomationTranslations = {
  en: {
    workflowAutomation: {
      heroEyebrow: "Kranbos Workflow Automation",
      heroTitle: "Stop Moving Work Between Systems by Hand",
      heroSubtitle:
        "Kranbos connects the tools your business already uses and automates the handoffs between them—so information, documents, tasks, approvals, and updates move forward without constant manual follow-up.",
      heroPrimaryCta: "Request a Workflow Audit",
      heroSecondaryCta: "See What We Automate",
      flowLabel: "A connected workflow",
      flowStep1: "New Request",
      flowStep2: "Information Collected",
      flowStep3: "Documents Sent",
      flowStep4: "Review",
      flowStep5: "Assignment",
      flowStep6: "Follow-Up",
      flowCaption: "Information moves between stages automatically.",

      problemEyebrow: "The real bottleneck",
      problemTitle: "Your tools may work. The gaps between them do not.",
      problemText1:
        "Most businesses already have software for email, customer management, documents, signatures, scheduling, projects, and reporting.",
      problemText2: "The problem is everything that happens between those systems.",
      problemText3:
        "Employees copy information from one platform to another, create the same records more than once, send repetitive reminders, update multiple spreadsheets, and rely on memory to keep work moving. These small manual steps create delays, inconsistent processes, duplicated work, and avoidable mistakes.",
      problemText4:
        "Kranbos identifies those gaps and builds a more connected workflow around the way your business actually operates.",
      gap1: "Copying customer information between systems",
      gap2: "Manually creating tasks and folders",
      gap3: "Chasing documents and approvals",
      gap4: "Sending repetitive status updates",
      gap5: "Checking multiple platforms for progress",
      gap6: "Rebuilding reports in spreadsheets",

      capabilitiesEyebrow: "What we can automate",
      capabilitiesTitle: "Remove repetitive work from your daily operations",
      capabilitiesIntro:
        "Every workflow is different. Kranbos starts by understanding the steps your team follows today, then identifies where automation can save time without removing the human judgment your business still needs.",
      cap1Title: "Intake and onboarding",
      cap1Text:
        "Capture information, send forms, request documents, assign team members, and create the appropriate customer, project, or service records.",
      cap2Title: "System-to-system handoffs",
      cap2Text:
        "Move information between your CRM, project tools, email, document storage, spreadsheets, scheduling platforms, and other business systems.",
      cap3Title: "Documents and signatures",
      cap3Text:
        "Generate documents, organize files, request signatures, track completion, and notify the right people when action is required.",
      cap4Title: "Tasks and follow-ups",
      cap4Text:
        "Create tasks, send reminders, escalate overdue items, and prevent important next steps from being forgotten.",
      cap5Title: "Approvals and status updates",
      cap5Text:
        "Build clear approval points and automatically communicate changes to employees, contractors, customers, or managers.",
      cap6Title: "Alerts and operational reporting",
      cap6Text:
        "Surface incomplete work, stalled processes, exceptions, and important activity without requiring someone to check every system manually.",

      systemsEyebrow: "Built around your business",
      systemsTitle: "Keep the systems that already work for you",
      systemsText1:
        "Workflow automation does not have to mean replacing every tool your business uses.",
      systemsText2:
        "Kranbos evaluates your existing software to determine what can be connected, what can be automated, and where a lightweight custom solution may be more practical.",
      system1: "CRM and customer management",
      system2: "Microsoft 365 and Outlook",
      system3: "Google Workspace",
      system4: "Adobe Acrobat and document storage",
      system5: "DocuSign and electronic signatures",
      system6: "Scheduling and project management",
      system7: "Spreadsheets and shared files",
      system8: "Accounting and invoicing platforms",
      system9: "Industry-specific software",
      system10: "Internal databases and tools",
      systemsNote:
        "When a platform does not provide a complete native integration, we evaluate available APIs, approved automation options, and lightweight internal tools to find the safest practical approach.",

      exampleEyebrow: "Example workflow",
      exampleTitle: "From a new property inquiry to coordinated service",
      exampleIntro1:
        "A property management company may receive information through email, phone calls, website forms, tenant portals, property owners, and maintenance requests. Without a connected workflow, employees may have to recreate records, send the same information repeatedly, and manually coordinate vendors and contractors.",
      exampleIntro2:
        "Kranbos can help create a more consistent process across intake, documents, approvals, work assignments, communications, and reporting.",
      step1Title: "Capture the request",
      step1Text:
        "A tenant, property owner, prospective client, or employee submits an inquiry, document, or maintenance request.",
      step2Title: "Collect the right information",
      step2Text:
        "The system sends the appropriate form, requests supporting documents, and follows up when required information is missing.",
      step3Title: "Review and approve",
      step3Text:
        "The appropriate manager is notified when the request is ready for review and can approve, reject, or request additional information.",
      step4Title: "Create and assign the work",
      step4Text:
        "Approved information is used to create the appropriate record, task, folder, work order, or project without unnecessary duplicate entry.",
      step5Title: "Coordinate the team",
      step5Text:
        "Employees, vendors, or contractors receive the specific tasks, deadlines, documents, and instructions relevant to their role.",
      step6Title: "Monitor exceptions",
      step6Text:
        "Managers can see which requests are incomplete, which tasks are overdue, which vendors have not responded, and where work is getting stuck.",
      exampleClosing:
        "The exact workflow depends on the capabilities and access provided by each platform. Kranbos begins by mapping the current process and identifying what can be automated reliably.",

      accessEyebrow: "Controlled access",
      accessTitle: "Give people access to the work—not everything else",
      accessText1:
        "Automation should not require giving every employee or contractor unrestricted access to sensitive business information.",
      accessText2:
        "Kranbos can help structure workflows around separate user accounts, limited permissions, approval steps, controlled document sharing, and clearer activity visibility.",
      accessText3:
        "This is especially useful for businesses working with outside contractors, virtual assistants, distributed teams, or overseas personnel.",
      access1Title: "Role-based access",
      access1Text: "Limit the systems, records, and actions available to each person.",
      access2Title: "Approval checkpoints",
      access2Text: "Require review before sensitive or irreversible actions are completed.",
      access3Title: "Controlled document handling",
      access3Text: "Reduce unnecessary downloading, forwarding, and duplication of business files.",
      access4Title: "Activity visibility",
      access4Text:
        "Create a clearer record of what happened, when it happened, and who completed it.",
      accessDisclaimer:
        "Kranbos implements practical operational controls as part of the solution. Formal compliance assessments and cybersecurity certifications may require a specialized security provider.",

      toolsEyebrow: "Beyond basic automation",
      toolsTitle: "Some workflow gaps need a custom interface",
      toolsText1: "Not every operational problem can be solved with a simple integration.",
      toolsText2:
        "Your team may need a secure portal, an internal intake screen, a central work queue, an administrative dashboard, or a tool that brings information from multiple systems into one place.",
      toolsText3:
        "When that happens, Kranbos can build a lightweight internal tool that gives your team a clearer and more efficient way to complete the work.",
      toolsCta: "Explore Internal Tools",

      processEyebrow: "How it works",
      processTitle: "Start with the workflow, not the technology",
      process1Title: "Workflow audit",
      process1Text:
        "We document the current process, systems, responsibilities, recurring problems, and manual workarounds.",
      process2Title: "Automation blueprint",
      process2Text:
        "You receive a recommended workflow showing what should be automated, what should remain manual, and where platform limitations need to be considered.",
      process3Title: "Build and testing",
      process3Text:
        "We configure integrations, automation rules, notifications, permissions, and any required supporting tools.",
      process4Title: "Launch and optimization",
      process4Text:
        "We deploy the workflow, document the process, help your team adopt it, and refine it based on real usage.",

      outcomesEyebrow: "The result",
      outcomesTitle: "A business that depends less on memory and manual follow-up",
      outcome1Title: "Fewer repetitive tasks",
      outcome1Text: "Reduce copying, updating, reminding, organizing, and checking.",
      outcome2Title: "Cleaner handoffs",
      outcome2Text: "Make ownership, responsibilities, and next steps clearer across the team.",
      outcome3Title: "Faster processing",
      outcome3Text: "Move requests, documents, approvals, and active work forward sooner.",
      outcome4Title: "Better visibility",
      outcome4Text: "See where work is progressing and where it is getting stuck.",

      relatedTitle: "One workflow may require more than one solution",
      relatedIntro:
        "Kranbos can combine automation, internal tools, and reporting into one practical solution without forcing your business into a completely new technology stack.",
      related1Title: "Workflow Automation",
      related1Text:
        "Connect systems and automatically move information and actions forward.",
      relatedCurrentLabel: "You are here",
      related2Title: "Internal Tools",
      related2Text: "Give your team a better interface for work that cannot be fully automated.",
      related3Title: "Dashboards & Reporting",
      related3Text: "See activity, workload, bottlenecks, and results across your operation.",
      relatedLearnMore: "Learn more",

      finalCtaTitle: "Find the manual work slowing your business down",
      finalCtaText:
        "We will review your current workflow, identify the biggest operational gaps, and determine where automation or a lightweight internal tool could have the greatest impact.",
      finalPrimaryCta: "Request a Workflow Audit",
      finalSecondaryCta: "Contact Us",
    },
  },
  es: {
    workflowAutomation: {
      heroEyebrow: "Automatización de Flujos Kranbos",
      heroTitle: "Deja de Mover el Trabajo Entre Sistemas a Mano",
      heroSubtitle:
        "Kranbos conecta las herramientas que tu negocio ya usa y automatiza los traspasos entre ellas—para que la información, los documentos, las tareas, las aprobaciones y las actualizaciones avancen sin seguimiento manual constante.",
      heroPrimaryCta: "Solicitar una Auditoría de Flujo",
      heroSecondaryCta: "Ver Qué Automatizamos",
      flowLabel: "Un flujo conectado",
      flowStep1: "Nueva Solicitud",
      flowStep2: "Información Recopilada",
      flowStep3: "Documentos Enviados",
      flowStep4: "Revisión",
      flowStep5: "Asignación",
      flowStep6: "Seguimiento",
      flowCaption: "La información avanza entre etapas automáticamente.",

      problemEyebrow: "El verdadero cuello de botella",
      problemTitle: "Tus herramientas pueden funcionar. Los huecos entre ellas no.",
      problemText1:
        "La mayoría de los negocios ya tienen software para correo, gestión de clientes, documentos, firmas, agendamiento, proyectos y reportes.",
      problemText2: "El problema es todo lo que ocurre entre esos sistemas.",
      problemText3:
        "Los empleados copian información de una plataforma a otra, crean los mismos registros más de una vez, envían recordatorios repetitivos, actualizan varias hojas de cálculo y dependen de la memoria para que el trabajo avance. Esos pequeños pasos manuales generan demoras, procesos inconsistentes, trabajo duplicado y errores evitables.",
      problemText4:
        "Kranbos identifica esos huecos y construye un flujo más conectado alrededor de la forma en que tu negocio realmente opera.",
      gap1: "Copiar información de clientes entre sistemas",
      gap2: "Crear tareas y carpetas manualmente",
      gap3: "Perseguir documentos y aprobaciones",
      gap4: "Enviar actualizaciones de estado repetitivas",
      gap5: "Revisar varias plataformas para ver el avance",
      gap6: "Rehacer reportes en hojas de cálculo",

      capabilitiesEyebrow: "Qué podemos automatizar",
      capabilitiesTitle: "Elimina el trabajo repetitivo de tu operación diaria",
      capabilitiesIntro:
        "Cada flujo es distinto. Kranbos empieza por entender los pasos que tu equipo sigue hoy y luego identifica dónde la automatización puede ahorrar tiempo sin eliminar el criterio humano que tu negocio sigue necesitando.",
      cap1Title: "Captación e incorporación",
      cap1Text:
        "Captura información, envía formularios, solicita documentos, asigna personas y crea los registros de cliente, proyecto o servicio correspondientes.",
      cap2Title: "Traspasos entre sistemas",
      cap2Text:
        "Mueve información entre tu CRM, herramientas de proyectos, correo, almacenamiento de documentos, hojas de cálculo, plataformas de agendamiento y otros sistemas del negocio.",
      cap3Title: "Documentos y firmas",
      cap3Text:
        "Genera documentos, organiza archivos, solicita firmas, controla su finalización y avisa a las personas correctas cuando se requiere una acción.",
      cap4Title: "Tareas y seguimientos",
      cap4Text:
        "Crea tareas, envía recordatorios, escala pendientes vencidos y evita que se olviden los siguientes pasos importantes.",
      cap5Title: "Aprobaciones y actualizaciones de estado",
      cap5Text:
        "Define puntos de aprobación claros y comunica automáticamente los cambios a empleados, contratistas, clientes o gerentes.",
      cap6Title: "Alertas y reportes operativos",
      cap6Text:
        "Muestra trabajo incompleto, procesos detenidos, excepciones y actividad importante sin que alguien tenga que revisar cada sistema manualmente.",

      systemsEyebrow: "Construido alrededor de tu negocio",
      systemsTitle: "Conserva los sistemas que ya te funcionan",
      systemsText1:
        "Automatizar flujos no tiene que significar reemplazar cada herramienta que tu negocio usa.",
      systemsText2:
        "Kranbos evalúa tu software actual para determinar qué se puede conectar, qué se puede automatizar y dónde una solución a medida y liviana puede ser más práctica.",
      system1: "CRM y gestión de clientes",
      system2: "Microsoft 365 y Outlook",
      system3: "Google Workspace",
      system4: "Adobe Acrobat y almacenamiento de documentos",
      system5: "DocuSign y firmas electrónicas",
      system6: "Agendamiento y gestión de proyectos",
      system7: "Hojas de cálculo y archivos compartidos",
      system8: "Plataformas de contabilidad y facturación",
      system9: "Software específico de la industria",
      system10: "Bases de datos y herramientas internas",
      systemsNote:
        "Cuando una plataforma no ofrece una integración nativa completa, evaluamos las APIs disponibles, las opciones de automatización aprobadas y herramientas internas livianas para encontrar el enfoque práctico más seguro.",

      exampleEyebrow: "Flujo de ejemplo",
      exampleTitle: "De una nueva consulta inmobiliaria a un servicio coordinado",
      exampleIntro1:
        "Una empresa de administración de propiedades puede recibir información por correo, llamadas, formularios web, portales de inquilinos, propietarios y solicitudes de mantenimiento. Sin un flujo conectado, los empleados pueden tener que recrear registros, enviar la misma información repetidamente y coordinar proveedores y contratistas de forma manual.",
      exampleIntro2:
        "Kranbos puede ayudar a crear un proceso más consistente en captación, documentos, aprobaciones, asignación de trabajo, comunicaciones y reportes.",
      step1Title: "Capturar la solicitud",
      step1Text:
        "Un inquilino, propietario, cliente potencial o empleado envía una consulta, un documento o una solicitud de mantenimiento.",
      step2Title: "Recopilar la información correcta",
      step2Text:
        "El sistema envía el formulario adecuado, solicita los documentos de respaldo y da seguimiento cuando falta información requerida.",
      step3Title: "Revisar y aprobar",
      step3Text:
        "Se notifica al gerente correspondiente cuando la solicitud está lista para revisión y puede aprobar, rechazar o pedir información adicional.",
      step4Title: "Crear y asignar el trabajo",
      step4Text:
        "La información aprobada se usa para crear el registro, la tarea, la carpeta, la orden de trabajo o el proyecto correspondiente sin carga duplicada innecesaria.",
      step5Title: "Coordinar al equipo",
      step5Text:
        "Empleados, proveedores o contratistas reciben las tareas, fechas, documentos e instrucciones específicas de su rol.",
      step6Title: "Monitorear excepciones",
      step6Text:
        "Los gerentes pueden ver qué solicitudes están incompletas, qué tareas están vencidas, qué proveedores no respondieron y dónde se traba el trabajo.",
      exampleClosing:
        "El flujo exacto depende de las capacidades y accesos que ofrece cada plataforma. Kranbos empieza mapeando el proceso actual e identificando qué se puede automatizar de forma confiable.",

      accessEyebrow: "Acceso controlado",
      accessTitle: "Da acceso al trabajo—no a todo lo demás",
      accessText1:
        "Automatizar no debería requerir dar a cada empleado o contratista acceso irrestricto a información sensible del negocio.",
      accessText2:
        "Kranbos puede ayudar a estructurar los flujos con cuentas de usuario separadas, permisos limitados, pasos de aprobación, uso controlado de documentos y mayor visibilidad de la actividad.",
      accessText3:
        "Esto es especialmente útil para negocios que trabajan con contratistas externos, asistentes virtuales, equipos distribuidos o personal en el exterior.",
      access1Title: "Acceso por rol",
      access1Text: "Limita los sistemas, registros y acciones disponibles para cada persona.",
      access2Title: "Puntos de aprobación",
      access2Text: "Exige revisión antes de completar acciones sensibles o irreversibles.",
      access3Title: "Manejo controlado de documentos",
      access3Text:
        "Reduce descargas, reenvíos y duplicaciones innecesarias de archivos del negocio.",
      access4Title: "Visibilidad de la actividad",
      access4Text: "Crea un registro más claro de qué pasó, cuándo pasó y quién lo completó.",
      accessDisclaimer:
        "Kranbos implementa controles operativos prácticos como parte de la solución. Las evaluaciones formales de cumplimiento y las certificaciones de ciberseguridad pueden requerir un proveedor de seguridad especializado.",

      toolsEyebrow: "Más allá de la automatización básica",
      toolsTitle: "Algunos huecos del flujo necesitan una interfaz a medida",
      toolsText1: "No todo problema operativo se resuelve con una integración simple.",
      toolsText2:
        "Tu equipo puede necesitar un portal seguro, una pantalla interna de captación, una cola central de trabajo, un panel administrativo o una herramienta que reúna información de varios sistemas en un solo lugar.",
      toolsText3:
        "Cuando eso ocurre, Kranbos puede construir una herramienta interna liviana que le dé a tu equipo una forma más clara y eficiente de completar el trabajo.",
      toolsCta: "Ver Herramientas Internas",

      processEyebrow: "Cómo funciona",
      processTitle: "Empieza por el flujo, no por la tecnología",
      process1Title: "Auditoría del flujo",
      process1Text:
        "Documentamos el proceso actual, los sistemas, las responsabilidades, los problemas recurrentes y las soluciones manuales.",
      process2Title: "Plan de automatización",
      process2Text:
        "Recibes un flujo recomendado que muestra qué debería automatizarse, qué debería seguir siendo manual y dónde hay que considerar limitaciones de las plataformas.",
      process3Title: "Construcción y pruebas",
      process3Text:
        "Configuramos integraciones, reglas de automatización, notificaciones, permisos y cualquier herramienta de apoyo necesaria.",
      process4Title: "Lanzamiento y optimización",
      process4Text:
        "Implementamos el flujo, documentamos el proceso, ayudamos a tu equipo a adoptarlo y lo ajustamos según el uso real.",

      outcomesEyebrow: "El resultado",
      outcomesTitle: "Un negocio que depende menos de la memoria y del seguimiento manual",
      outcome1Title: "Menos tareas repetitivas",
      outcome1Text: "Reduce copiar, actualizar, recordar, organizar y revisar.",
      outcome2Title: "Traspasos más limpios",
      outcome2Text:
        "Deja más claros los responsables, las obligaciones y los siguientes pasos en el equipo.",
      outcome3Title: "Procesamiento más rápido",
      outcome3Text: "Haz avanzar antes solicitudes, documentos, aprobaciones y trabajo activo.",
      outcome4Title: "Mejor visibilidad",
      outcome4Text: "Mira dónde avanza el trabajo y dónde se está trabando.",

      relatedTitle: "Un flujo puede requerir más de una solución",
      relatedIntro:
        "Kranbos puede combinar automatización, herramientas internas y reportes en una sola solución práctica sin obligar a tu negocio a cambiar por completo de tecnología.",
      related1Title: "Automatización de Flujos",
      related1Text: "Conecta sistemas y haz avanzar información y acciones automáticamente.",
      relatedCurrentLabel: "Estás aquí",
      related2Title: "Herramientas Internas",
      related2Text:
        "Dale a tu equipo una mejor interfaz para el trabajo que no se puede automatizar por completo.",
      related3Title: "Paneles y Reportes",
      related3Text: "Mira actividad, carga de trabajo, cuellos de botella y resultados.",
      relatedLearnMore: "Saber más",

      finalCtaTitle: "Encuentra el trabajo manual que frena a tu negocio",
      finalCtaText:
        "Revisaremos tu flujo actual, identificaremos los mayores huecos operativos y determinaremos dónde la automatización o una herramienta interna liviana tendrían el mayor impacto.",
      finalPrimaryCta: "Solicitar una Auditoría de Flujo",
      finalSecondaryCta: "Contáctanos",
    },
  },
};
